
import React, { useState, useEffect } from 'react';
import { Button, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { useLeague } from '../components/LeagueContext';
import { Calculator, Trophy, RefreshCw, Zap, Shield, Star, ChevronDown, ChevronUp, Flag, Check } from 'lucide-react';
import { PageHero } from '../components/PageHero';

interface Driver {
    id: string; username: string; teamName: string; teamColor: string; totalPoints: number;
}

interface CalendarEvent {
    id: string; trackLocation: string; roundNumber: number; isSprint: boolean; distance: string; status: string; date: string;
}

interface SimResult {
    driverId: string; position: number; fastestLap: boolean; cleanRace: boolean; dotd: boolean; dnf: boolean;
    sprintPosition: number; sprintDnf: boolean;
}

interface SimRace {
    eventId: string; label: string; isSprint: boolean; distance: string; results: SimResult[]; enabled: boolean;
}

export const ChampionshipCalculator: React.FC = () => {
    const { settings } = useLeague();
    const [drivers, setDrivers] = useState<Driver[]>([]);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [simRaces, setSimRaces] = useState<SimRace[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedRace, setExpandedRace] = useState<string | null>(null);

    // Points from settings
    const pts50: number[] = Array.isArray(settings.points_50) ? settings.points_50 : (() => { try { return JSON.parse(settings.points_50 as any || '[]'); } catch { return [25,18,15,12,10,8,6,4,2,1]; }})();
    const pts100: number[] = Array.isArray(settings.points_100) ? settings.points_100 : (() => { try { return JSON.parse(settings.points_100 as any || '[]'); } catch { return [50,36,30,24,20,16,12,8,4,2]; }})();
    const ptsSprint: number[] = Array.isArray(settings.points_sprint) ? settings.points_sprint : (() => { try { return JSON.parse(settings.points_sprint as any || '[]'); } catch { return [8,7,6,5,4,3,2,1]; }})();
    const flPoint = Number(settings.point_fastest_lap ?? 1);
    const dotdPoint = Number(settings.point_dotd ?? 1);
    const cleanPoint = 1;

    useEffect(() => {
        Promise.all([
            apiRequest<any>('standings.php').catch(() => ({ drivers: [] })),
            apiRequest<any[]>('events.php').catch(() => [])
        ]).then(([standingsRes, eventsRes]) => {
            if (standingsRes?.drivers) {
                setDrivers(standingsRes.drivers.map((d: any) => ({
                    id: d.id, username: d.username, teamName: d.teamName || 'Reserve',
                    teamColor: d.teamColor || '#666', totalPoints: Number(d.totalPoints || 0)
                })));
            }
            const upcoming = (Array.isArray(eventsRes) ? eventsRes : [])
                .filter((e: any) => e.status !== 'COMPLETED')
                .sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime())
                .map((e: any) => ({
                    id: e.id, trackLocation: e.trackLocation, roundNumber: e.roundNumber,
                    isSprint: e.isSprint === true || e.isSprint === 1 || e.isSprint === '1',
                    distance: e.distance || '50%', status: e.status, date: e.date
                }));
            setEvents(upcoming);

            // Auto-create sim for each upcoming event
            if (standingsRes?.drivers) {
                const drvList = standingsRes.drivers;
                const sims = upcoming.map((evt: CalendarEvent) => ({
                    eventId: evt.id,
                    label: `R${evt.roundNumber}: ${evt.trackLocation}`,
                    isSprint: evt.isSprint,
                    distance: evt.distance,
                    enabled: false,
                    results: Array.from({ length: 20 }, (_, i) => ({
                        driverId: '', position: i + 1, fastestLap: false, cleanRace: false, dotd: false, dnf: false,
                        sprintPosition: i + 1, sprintDnf: false
                    }))
                }));
                setSimRaces(sims);
            }
            setLoading(false);
        });
    }, []);

    const toggleRace = (eventId: string) => {
        setSimRaces(prev => prev.map(r => r.eventId === eventId ? { ...r, enabled: !r.enabled } : r));
    };

    const updateResult = (eventId: string, driverId: string, field: keyof SimResult, value: any) => {
        setSimRaces(prev => prev.map(race => {
            if (race.eventId !== eventId) return race;
            let newResults = race.results.map(r => r.driverId === driverId ? { ...r, [field]: value } : r);
            if (field === 'fastestLap' && value === true) newResults = newResults.map(r => r.driverId === driverId ? r : { ...r, fastestLap: false });
            if (field === 'dotd' && value === true) newResults = newResults.map(r => r.driverId === driverId ? r : { ...r, dotd: false });
            return { ...race, results: newResults };
        }));
    };

    const setDriverAtPosition = (eventId: string, posIndex: number, newDriverId: string) => {
        setSimRaces(prev => prev.map(race => {
            if (race.eventId !== eventId) return race;
            const newResults = race.results.map((r, i) => {
                // Clear this driver from any other position first
                if (r.driverId === newDriverId && i !== posIndex) return { ...r, driverId: '' };
                if (i === posIndex) return { ...r, driverId: newDriverId };
                return r;
            });
            return { ...race, results: newResults };
        }));
    };

    const moveDriver = (eventId: string, driverId: string, targetPos: number, sprint: boolean) => {
        setSimRaces(prev => prev.map(race => {
            if (race.eventId !== eventId) return race;
            const results = [...race.results];
            const field = sprint ? 'sprintPosition' : 'position';
            const fromIdx = results.findIndex(r => r.driverId === driverId);
            if (fromIdx === -1) return race;

            // Reorder by current field to get proper ordering
            const sorted = [...results].sort((a, b) => a[field] - b[field]);
            const item = sorted.splice(sorted.findIndex(r => r.driverId === driverId), 1)[0];
            sorted.splice(targetPos - 1, 0, item);
            const posMap: Record<string, number> = {};
            sorted.forEach((r, i) => posMap[r.driverId] = i + 1);

            return { ...race, results: results.map(r => ({ ...r, [field]: posMap[r.driverId] ?? r[field] })) };
        }));
    };

    const calcSimPoints = (driverId: string): number => {
        let total = 0;
        for (const race of simRaces) {
            if (!race.enabled) continue;
            const res = race.results.find(r => r.driverId === driverId);
            if (!res) continue;

            // Race points
            if (!res.dnf) {
                const raceSystem = race.distance === '100%' ? pts100 : pts50;
                const posIdx = res.position - 1;
                if (posIdx >= 0 && posIdx < raceSystem.length) total += raceSystem[posIdx];
                if (res.fastestLap) total += flPoint;
                if (res.cleanRace) total += cleanPoint;
                if (res.dotd) total += dotdPoint;
            }

            // Sprint points
            if (race.isSprint && !res.sprintDnf) {
                const spIdx = res.sprintPosition - 1;
                if (spIdx >= 0 && spIdx < ptsSprint.length) total += ptsSprint[spIdx];
            }
        }
        return total;
    };

    const combinedStandings = drivers.map(d => ({
        ...d, simPoints: calcSimPoints(d.id), newTotal: d.totalPoints + calcSimPoints(d.id)
    })).sort((a, b) => b.newTotal - a.newTotal);

    const originalOrder = [...drivers].sort((a, b) => b.totalPoints - a.totalPoints);
    const getOriginalPos = (id: string) => originalOrder.findIndex(d => d.id === id) + 1;
    const enabledCount = simRaces.filter(r => r.enabled).length;

    if (loading) return <div className="py-20 text-center"><RefreshCw className="animate-spin mx-auto text-gray-600" /></div>;

    return (
        <div className="sdc-feature-page sdc-calculator-page max-w-7xl mx-auto space-y-6 animate-fade-in pb-20">
            <PageHero
                eyebrow="Championship Scenario Lab"
                title="WM-Rechner"
                description="Teste mögliche Rennausgänge, verteile Bonuspunkte und sieh sofort, wie sich jedes Szenario auf die Weltmeisterschaft auswirkt."
                icon={Calculator}
                accent="#eab308"
                stats={[{ label: 'Fahrer', value: drivers.length }, { label: 'Rennen', value: events.length }, { label: 'Aktiv', value: enabledCount }]}
            >
                <div className="mt-5 flex flex-wrap gap-2 text-[8px] font-black uppercase tracking-wider text-white/30"><span className="rounded-full border border-white/[0.08] bg-black/20 px-3 py-1.5">50% · {pts50.slice(0,3).join(' / ')}</span><span className="rounded-full border border-white/[0.08] bg-black/20 px-3 py-1.5">100% · {pts100.slice(0,3).join(' / ')}</span><span className="rounded-full border border-white/[0.08] bg-black/20 px-3 py-1.5">Sprint · {ptsSprint.slice(0,3).join(' / ')}</span></div>
            </PageHero>

            {events.length === 0 && (
                <div className="rounded-xl border-2 border-dashed border-gray-800/40 p-12 text-center">
                    <Calculator size={40} className="mx-auto mb-3 text-gray-700" />
                    <p className="text-gray-600 text-sm">Keine anstehenden Rennen im Kalender.</p>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* LEFT: Race Simulations */}
                <div className="lg:col-span-2 space-y-3">
                    {simRaces.map(race => {
                        const isExpanded = expandedRace === race.eventId;
                        const evt = events.find(e => e.id === race.eventId);
                        const raceSystem = race.distance === '100%' ? pts100 : pts50;

                        return (
                            <div key={race.eventId} className={`rounded-xl border overflow-hidden transition-all ${race.enabled ? 'border-yellow-500/30 bg-[#12121a]' : 'border-gray-800/40 bg-[#12121a] opacity-60'}`}>
                                <div className="h-[2px]" style={{ background: race.enabled ? 'linear-gradient(90deg, #eab308, transparent)' : 'linear-gradient(90deg, #333, transparent)' }} />

                                {/* Race Header */}
                                <div className="px-4 py-3 flex items-center gap-3">
                                    <button onClick={() => toggleRace(race.eventId)} className={`w-6 h-6 rounded border flex items-center justify-center transition-colors ${race.enabled ? 'bg-yellow-500 border-yellow-500 text-black' : 'bg-gray-800 border-gray-700 text-gray-600 hover:border-gray-500'}`}>
                                        {race.enabled && <Check size={14} />}
                                    </button>
                                    <div className="flex-1 cursor-pointer" onClick={() => setExpandedRace(isExpanded ? null : race.eventId)}>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-black text-white">{race.label}</span>
                                            {race.isSprint && <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30 text-[8px]">SPRINT</Badge>}
                                            {race.distance === '100%' && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[8px]">100%</Badge>}
                                        </div>
                                        {evt && <span className="text-[9px] text-gray-600">{new Date(evt.date).toLocaleDateString('de-DE')}</span>}
                                    </div>
                                    <button onClick={() => setExpandedRace(isExpanded ? null : race.eventId)} className="text-gray-600 hover:text-white">
                                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                </div>

                                {/* Expanded: Results Editor */}
                                {isExpanded && race.enabled && (
                                    <div className="px-4 pb-4">
                                        {/* Race Tab */}
                                        <div className="mb-3 flex items-center gap-2 text-[9px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-800/30 pb-2">
                                            <Flag size={10} className="text-f1-red" /> Rennergebnis {race.distance === '100%' ? '(Doppelte Punkte)' : ''}
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left">
                                                <thead><tr className="text-[8px] font-bold text-gray-700 uppercase tracking-wider">
                                                    <th className="px-2 py-1 w-8">P</th><th className="px-2 py-1">Fahrer</th><th className="px-2 py-1 w-12 text-center">Pkt</th>
                                                    <th className="px-2 py-1 w-7 text-center" title="Fastest Lap">FL</th><th className="px-2 py-1 w-7 text-center" title="Clean Race">CR</th>
                                                    <th className="px-2 py-1 w-7 text-center" title="Driver of the Day">★</th><th className="px-2 py-1 w-7 text-center">DNF</th>
                                                </tr></thead>
                                                <tbody>
                                                    {race.results.map((res, idx) => {
                                                        const d = res.driverId ? drivers.find(x => x.id === res.driverId) : null;
                                                        const posIdx = res.position - 1;
                                                        const pts = !res.driverId || res.dnf ? 0 : (posIdx >= 0 && posIdx < raceSystem.length ? raceSystem[posIdx] : 0) + (res.fastestLap ? flPoint : 0) + (res.cleanRace ? cleanPoint : 0) + (res.dotd ? dotdPoint : 0);
                                                        const usedIds = race.results.filter((r, i) => i !== idx && r.driverId).map(r => r.driverId);
                                                        
                                                        return (
                                                            <tr key={idx} className="border-t border-gray-800/10 hover:bg-white/[0.02]">
                                                                <td className="px-2 py-1"><span className={`font-mono font-black text-[11px] ${idx<3?'text-yellow-400':'text-gray-600'}`}>{res.position}</span></td>
                                                                <td className="px-2 py-1">
                                                                    <select className={`bg-[#0e0e16] border rounded p-1 text-[10px] text-white w-full max-w-[160px] outline-none ${res.driverId ? 'border-green-500/30' : 'border-gray-800/40'}`} value={res.driverId} onChange={e => setDriverAtPosition(race.eventId, idx, e.target.value)}>
                                                                        <option value="">-- Leer --</option>
                                                                        {drivers.filter(dr => !usedIds.includes(dr.id) || dr.id === res.driverId).map(dr => (
                                                                            <option key={dr.id} value={dr.id}>{dr.username} ({dr.teamName})</option>
                                                                        ))}
                                                                    </select>
                                                                </td>
                                                                <td className="px-2 py-1 text-center"><span className={`font-mono font-bold text-[10px] ${pts>0?'text-green-400':'text-gray-700'}`}>{res.driverId ? `+${pts}` : '-'}</span></td>
                                                                <td className="px-2 py-1 text-center"><input type="checkbox" checked={res.fastestLap} onChange={()=>{if(res.driverId) updateResult(race.eventId,res.driverId,'fastestLap',!res.fastestLap)}} disabled={!res.driverId||res.dnf} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-purple-500 focus:ring-0 cursor-pointer disabled:opacity-20"/></td>
                                                                <td className="px-2 py-1 text-center"><input type="checkbox" checked={res.cleanRace} onChange={()=>{if(res.driverId) updateResult(race.eventId,res.driverId,'cleanRace',!res.cleanRace)}} disabled={!res.driverId||res.dnf} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-green-500 focus:ring-0 cursor-pointer disabled:opacity-20"/></td>
                                                                <td className="px-2 py-1 text-center"><input type="checkbox" checked={res.dotd} onChange={()=>{if(res.driverId) updateResult(race.eventId,res.driverId,'dotd',!res.dotd)}} disabled={!res.driverId||res.dnf} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-yellow-500 focus:ring-0 cursor-pointer disabled:opacity-20"/></td>
                                                                <td className="px-2 py-1 text-center"><input type="checkbox" checked={res.dnf} onChange={()=>{if(res.driverId) updateResult(race.eventId,res.driverId,'dnf',!res.dnf)}} disabled={!res.driverId} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-red-500 focus:ring-0 cursor-pointer disabled:opacity-20"/></td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Sprint Tab (only if sprint event) */}
                                        {race.isSprint && (
                                            <>
                                                <div className="mt-4 mb-3 flex items-center gap-2 text-[9px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-800/30 pb-2">
                                                    <Zap size={10} className="text-yellow-500" /> Sprint-Ergebnis
                                                </div>
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left">
                                                        <thead><tr className="text-[8px] font-bold text-gray-700 uppercase tracking-wider">
                                                            <th className="px-2 py-1 w-8">P</th><th className="px-2 py-1">Fahrer</th><th className="px-2 py-1 w-12 text-center">Pkt</th>
                                                            <th className="px-2 py-1 w-7 text-center">DNF</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {race.results.map((res, idx) => {
                                                                const d = res.driverId ? drivers.find(x => x.id === res.driverId) : null;
                                                                const spIdx = res.sprintPosition - 1;
                                                                const spPts = !res.driverId || res.sprintDnf ? 0 : (spIdx >= 0 && spIdx < ptsSprint.length ? ptsSprint[spIdx] : 0);
                                                                return (
                                                                    <tr key={idx} className="border-t border-gray-800/10 hover:bg-white/[0.02]">
                                                                        <td className="px-2 py-1"><span className={`font-mono font-black text-[11px] ${idx<3?'text-yellow-400':'text-gray-600'}`}>{res.sprintPosition}</span></td>
                                                                        <td className="px-2 py-1 text-[10px] text-gray-400">{d ? d.username : <span className="text-gray-700 italic">Leer</span>}</td>
                                                                        <td className="px-2 py-1 text-center"><span className={`font-mono font-bold text-[10px] ${spPts>0?'text-yellow-400':'text-gray-700'}`}>{res.driverId ? `+${spPts}` : '-'}</span></td>
                                                                        <td className="px-2 py-1 text-center"><input type="checkbox" checked={res.sprintDnf} onChange={()=>{if(res.driverId) updateResult(race.eventId,res.driverId,'sprintDnf',!res.sprintDnf)}} disabled={!res.driverId} className="w-3 h-3 rounded bg-gray-800 border-gray-600 text-red-500 focus:ring-0 cursor-pointer disabled:opacity-20"/></td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {isExpanded && !race.enabled && (
                                    <div className="px-4 pb-4 text-center text-gray-600 text-xs py-6">
                                        Aktiviere dieses Rennen mit der Checkbox um Ergebnisse zu simulieren.
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* RIGHT: Live Standings */}
                <div>
                    <div className="rounded-xl border border-gray-800/40 bg-[#12121a] overflow-hidden sticky top-4">
                        <div className="h-[2px] bg-gradient-to-r from-f1-red via-f1-red/50 to-transparent" />
                        <div className="px-4 py-3 border-b border-gray-800/30 flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-[0.15em] text-white/90">WM-Stand</span>
                            {enabledCount > 0 && <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/30 text-[9px]">+{enabledCount} Sim</Badge>}
                        </div>
                        <div className="max-h-[75vh] overflow-y-auto">
                            {combinedStandings.map((d, idx) => {
                                const origPos = getOriginalPos(d.id);
                                const posDiff = origPos - (idx + 1);
                                return (
                                    <div key={d.id} className={`flex items-center gap-2 px-3 py-2 border-b border-gray-800/10 ${d.simPoints > 0 ? 'bg-yellow-500/[0.03]' : ''} hover:bg-white/[0.02]`}>
                                        <div className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-black shrink-0 ${idx===0?'bg-gradient-to-br from-yellow-400 to-amber-600 text-black':idx===1?'bg-gradient-to-br from-gray-300 to-gray-500 text-black':idx===2?'bg-gradient-to-br from-orange-400 to-orange-700 text-black':'bg-gray-800 text-gray-500'}`}>{idx+1}</div>
                                        {enabledCount > 0 && posDiff !== 0 ? (
                                            <span className={`text-[8px] font-bold w-4 ${posDiff>0?'text-green-500':'text-red-500'}`}>{posDiff>0?`↑${posDiff}`:`↓${Math.abs(posDiff)}`}</span>
                                        ) : <span className="w-4"/>}
                                        <div className="w-0.5 h-4 rounded-full shrink-0" style={{backgroundColor:d.teamColor}}/>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[10px] font-bold text-white truncate">{d.username}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="font-mono font-black text-white text-[11px]">{d.newTotal}</div>
                                            {d.simPoints > 0 && <div className="text-[8px] text-green-500 font-bold">+{d.simPoints}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
